// hub → Studio → Run a product (CEO 2026-10-09).
//
// One product, every step that makes a supplier item ours, each with the
// supplier's original beside our result and its own button:
//   Photos       per colour and view, engine + backdrop pickers; every result
//                is checked automatically (Pass/Fail with the reason); Accept
//                makes it the live studio photo, Reject throws it away
//   Description  model picker; draft beside the supplier's words; accept, edit, reject
//   Name / SKU / Shelf   the clean name, the catalogue SKU, the shelf
// "Compare engines" runs 2–3 engines on the same photo or description side by
// side with their scores, cost and time; the pick is recorded so we learn
// which engine wins where. "Run all" does the lot with the default engines.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Check, ExternalLink, Loader2, Play, Scale, Search, X } from "lucide-react";
import { Link } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import {
  VIEW_LABEL, duration, hex, isOpen, money, newKey, photoScores, statusWords,
  type EngineCard, type EnginesInfo, type Estimate, type Job, type Run, type WorkbenchProduct,
} from "./workbench";

interface SearchHit { id: string; name: string; sku: string | null; supplier: string | null; image: string | null; ready: boolean }

export function RunProductTab({ productId, onOpen }: { productId: string | null; onOpen: (id: string | null) => void }) {
  return (
    <div className="mt-bay flex flex-col gap-gutter">
      <ProductSearch onOpen={onOpen} compact={!!productId} />
      {productId ? <ProductWorkbench key={productId} id={productId} /> : <JobsList />}
    </div>
  );
}

// ─── Search ──────────────────────────────────────────────────────────────────

function ProductSearch({ onOpen, compact }: { onOpen: (id: string | null) => void; compact: boolean }) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const box = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "/" && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement)) { e.preventDefault(); box.current?.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    if (q.trim().length < 2) { setHits(null); return; }
    const t = setTimeout(() => {
      api<{ results: SearchHit[] }>(`/studio/workbench/search?q=${encodeURIComponent(q.trim())}`)
        .then((r) => { setHits(r.results); setError(null); })
        .catch((e) => setError(apiErrorMessage(e, "Search failed.")));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  const pick = (id: string) => { setQ(""); setHits(null); onOpen(id); };
  return (
    <section className={cn("flex flex-col gap-2", !compact && "rounded-lg border border-rule bg-white p-5")}>
      {!compact && (
        <>
          <h2 className="text-label font-bold">Run a product through Studio</h2>
          <p className="max-w-[70ch] text-meta text-ink-muted">
            Find a product, then make it ours step by step: studio photos, our description, a clean name, its SKU and the right shelf.
            Every result is checked before you can accept it, and nothing changes on the website until you do.
            You can also open any product here from Products.
          </p>
        </>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <label className="relative min-w-[240px] flex-1">
          <span className="sr-only">Find a product</span>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" aria-hidden />
          <input
            ref={box}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && hits?.[0]) pick(hits[0].id); if (e.key === "Escape") setQ(""); }}
            placeholder={compact ? "Open another product…  ( / )" : "Name, SKU or supplier style number  ( / )"}
            className="h-10 w-full rounded-md border border-rule bg-white pl-9 pr-3 text-label placeholder:text-ink-muted/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus"
          />
        </label>
        {compact && <Button size="sm" variant="ghost" onClick={() => onOpen(null)}><X className="h-3.5 w-3.5" aria-hidden /> Close product</Button>}
      </div>
      {error && <p className="text-meta text-risk">{error}</p>}
      {hits && (
        <ul className="divide-y divide-rule rounded-lg border border-rule bg-white" role="listbox" aria-label="Matching products">
          {hits.length === 0 && <li className="p-3 text-meta text-ink-muted">No product matches “{q.trim()}”.</li>}
          {hits.map((h) => (
            <li key={h.id}>
              <button type="button" onClick={() => pick(h.id)} className="flex w-full items-center gap-3 p-3 text-left hover:bg-paper focus-visible:bg-paper focus-visible:outline-none">
                <span className="h-10 w-10 shrink-0 overflow-hidden rounded border border-rule bg-paper">{h.image && <img src={h.image} alt="" className="h-full w-full object-contain" loading="lazy" />}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-label font-semibold">{h.name}</span>
                  <span className="text-meta text-ink-muted">{[h.sku ?? "No SKU yet", h.supplier].filter(Boolean).join(" · ")}{h.ready ? " · on the website" : ""}</span>
                </span>
                <ArrowRight className="h-4 w-4 text-ink-muted" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── Batch runs started from Products ────────────────────────────────────────

function JobsList() {
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [notSetUp, setNotSetUp] = useState(false);
  useEffect(() => {
    let alive = true;
    const load = () => api<{ jobs: Job[]; notSetUp?: boolean }>("/studio/workbench/jobs").then((r) => { if (alive) { setJobs(r.jobs); setNotSetUp(!!r.notSetUp); } }).catch(() => alive && setJobs([]));
    load();
    const t = setInterval(load, 5000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  if (!jobs || (!jobs.length && !notSetUp)) return null;
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-label font-bold">Batches started from Products</h2>
      {notSetUp && <p className="text-meta text-ink-muted">The Studio workbench is not set up on this database yet.</p>}
      <ul className="divide-y divide-rule rounded-lg border border-rule bg-white">
        {jobs.map((j) => (
          <li key={j.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
            <span className="min-w-[220px] flex-1 text-label font-semibold">{j.label}</span>
            <span className="text-meta text-ink-muted">{j.status === "queued" ? "Getting ready…" : `${j.done} of ${j.total} done${j.failed ? ` · ${j.failed} failed checks` : ""}`} · about {money(j.costEstimateUsd)}</span>
            <span className="h-1.5 w-32 overflow-hidden rounded bg-paper" aria-hidden><span className="block h-full bg-ink" style={{ width: `${j.total ? Math.round((j.done / j.total) * 100) : 0}%` }} /></span>
            <span className={cn("rounded px-2 py-0.5 text-meta font-semibold", j.status === "done" ? "bg-ok-bg text-ok" : "bg-paper text-ink-muted")}>{j.status === "done" ? "Ready for review" : "Running"}</span>
            {j.status === "done" && (
              <span className="flex gap-3 text-meta">
                {j.steps.photos && <Link className="underline underline-offset-2" to="/studio?tab=studio">Review photos</Link>}
                {j.steps.description && <Link className="underline underline-offset-2" to="/studio?tab=descriptions">Review descriptions</Link>}
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

// ─── One product ─────────────────────────────────────────────────────────────

function ProductWorkbench({ id }: { id: string }) {
  const [p, setP] = useState<WorkbenchProduct | null>(null);
  const [eng, setEng] = useState<EnginesInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const load = useCallback(() => api<WorkbenchProduct>(`/studio/workbench/products/${id}`).then((x) => { setP(x); setError(null); }).catch((e) => setError(apiErrorMessage(e, "Could not load this product."))), [id]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { api<EnginesInfo>("/studio/workbench/engines").then(setEng).catch(() => {}); }, []);
  const busy = p?.runs.some(isOpen) ?? false;
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(() => void load(), 3000);
    return () => clearInterval(t);
  }, [busy, load]);

  const say = (kind: "ok" | "error", text: string) => setNote({ kind, text });
  const post = async (path: string, body?: unknown, ok?: string): Promise<boolean> => {
    try {
      await api(path, { method: "POST", body: JSON.stringify(body ?? {}) });
      if (ok) say("ok", ok);
      await load();
      return true;
    } catch (e) {
      say("error", apiErrorMessage(e, "That didn't work."));
      return false;
    }
  };
  const runs = async (body: Record<string, unknown>, ok: string) => post(`/studio/workbench/products/${id}/runs`, { requestKey: newKey(), ...body }, ok);

  if (error) return <p className="text-label text-risk">{error}</p>;
  if (!p) return <p className="flex items-center gap-2 text-label text-ink-muted"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading the product…</p>;
  const ctx: Ctx = { p, eng, runs, post, say };
  return (
    <div className="flex flex-col gap-gutter">
      <header className="flex flex-wrap items-start gap-4 rounded-lg border border-rule bg-white p-5">
        <div className="min-w-0 flex-1">
          <p className="text-meta text-ink-muted">{[p.sku ?? "No SKU yet", p.supplier, p.supplierStyle && `style ${p.supplierStyle}`, p.department?.name].filter(Boolean).join(" · ")}</p>
          <h2 className="text-[22px] font-semibold leading-tight">{p.name}</h2>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-meta">
            <span className={cn("rounded px-2 py-0.5 font-semibold", p.ready ? "bg-ok-bg text-ok" : "bg-warn-bg text-warn")}>{p.ready ? "On the website" : "Not on the website"}</span>
            <Link to={`/products?product=${p.id}`} className="inline-flex items-center gap-1 underline underline-offset-2">Open in Products <ExternalLink className="h-3 w-3" aria-hidden /></Link>
          </p>
        </div>
        <RunAll ctx={ctx} />
      </header>
      {!p.workbenchReady && <p className="rounded-lg border border-warn/30 bg-warn-bg px-4 py-3 text-label text-warn">The Studio workbench is not set up on this database yet, so runs can't start. Name, SKU and shelf work now.</p>}
      {p.worker.throttle && (
        <p role="status" className="rounded-lg border border-warn/30 bg-warn-bg px-4 py-3 text-label text-warn">{p.worker.throttle.message}</p>
      )}
      {note && (
        <p role={note.kind === "error" ? "alert" : "status"} className={cn("flex items-center gap-2 rounded-lg px-4 py-2 text-label", note.kind === "error" ? "bg-risk-bg text-risk" : "bg-ok-bg text-ok")}>
          {note.text}
          <button type="button" aria-label="Dismiss" className="ml-auto rounded p-1 hover:bg-white/50" onClick={() => setNote(null)}><X className="h-3.5 w-3.5" /></button>
        </p>
      )}
      <PhotosStep ctx={ctx} />
      <DescriptionStep ctx={ctx} />
      <div className="grid gap-gutter lg:grid-cols-3">
        <NameStep ctx={ctx} />
        <SkuStep ctx={ctx} />
        <ShelfStep ctx={ctx} />
      </div>
    </div>
  );
}

interface Ctx {
  p: WorkbenchProduct;
  eng: EnginesInfo | null;
  runs: (body: Record<string, unknown>, ok: string) => Promise<boolean>;
  post: (path: string, body?: unknown, ok?: string) => Promise<boolean>;
  say: (kind: "ok" | "error", text: string) => void;
}

function Step({ title, hint, actions, children }: { title: string; hint?: string; actions?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-4 rounded-lg border border-rule bg-white p-5">
      <header className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-label font-bold">{title}</h3>
          {hint && <p className="text-meta text-ink-muted">{hint}</p>}
        </div>
        {actions}
      </header>
      {children}
    </section>
  );
}

function Chip({ run }: { run: Run }) {
  const s = statusWords(run);
  return (
    <span className={cn("inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[12px] font-semibold",
      s.tone === "ok" ? "bg-ok-bg text-ok" : s.tone === "risk" ? "bg-risk-bg text-risk" : s.tone === "warn" ? "bg-warn-bg text-warn" : "bg-paper text-ink-muted")}>
      {isOpen(run) && <Loader2 className="h-3 w-3 animate-spin" aria-hidden />} {s.text}
    </span>
  );
}

function EngineSelect({ label, value, onChange, cards, disabledIds = [] }: { label: string; value: string; onChange: (v: string) => void; cards: EngineCard[]; disabledIds?: string[] }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-meta font-semibold uppercase tracking-wide text-ink-muted">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="h-9 max-w-[320px] rounded-md border border-rule bg-white px-2 text-meta">
        {cards.map((c) => (
          <option key={c.id} value={c.id} disabled={!c.ready || disabledIds.includes(c.id)}>
            {c.label} · ~{money(c.costUsd)}{!c.ready ? ` — add a key (${c.missing.join(", ")})` : ""}
          </option>
        ))}
      </select>
    </label>
  );
}

// ─── Run all ─────────────────────────────────────────────────────────────────

function RunAll({ ctx }: { ctx: Ctx }) {
  const { p } = ctx;
  const [est, setEst] = useState<Estimate | null>(null);
  const [busy, setBusy] = useState(false);
  const body = { photo: { engines: [p.defaults.photo.engine], backdrop: p.defaults.photo.backdrop, views: p.defaults.photo.views }, description: { engines: [p.defaults.description] } };
  const ask = async () => {
    setBusy(true);
    try { setEst((await api<{ estimate: Estimate }>(`/studio/workbench/products/${p.id}/runs`, { method: "POST", body: JSON.stringify({ requestKey: newKey(), dryRun: true, ...body }) })).estimate); }
    catch (e) { ctx.say("error", apiErrorMessage(e, "Could not work out the cost.")); }
    finally { setBusy(false); }
  };
  const go = async () => { setBusy(true); await ctx.runs(body, "Running every colour and the description. Results appear below as they finish."); setEst(null); setBusy(false); };
  if (est) {
    return (
      <div className="flex max-w-[460px] flex-col gap-2 rounded-lg border border-ink bg-paper p-3">
        <span className="text-label">Run {est.photos} photo{est.photos === 1 ? "" : "s"} ({p.defaults.photo.views.map((v) => VIEW_LABEL[v] ?? v).join(", ").toLowerCase()} of every colour, on {p.defaults.photo.backdrop}) and {est.descriptions} description with our default engines?</span>
        <span className="text-meta text-ink-muted">About {money(est.costUsd)} · about {est.minutes} min. Nothing goes live until you accept.</span>
        <span className="flex gap-2">
          <Button size="sm" disabled={busy || (!est.photos && !est.descriptions)} onClick={() => void go()}><Play className="h-3.5 w-3.5" aria-hidden /> Run all</Button>
          <Button size="sm" variant="ghost" onClick={() => setEst(null)}>Cancel</Button>
        </span>
      </div>
    );
  }
  return <Button onClick={() => void ask()} disabled={busy || !p.workbenchReady}>{busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Play className="h-4 w-4" aria-hidden />} Run all</Button>;
}

// ─── Photos ──────────────────────────────────────────────────────────────────

/** A comparison is still open while more than one of its results is undecided. */
function openComparisons(runs: Run[]): Map<string, Run[]> {
  const g = new Map<string, Run[]>();
  for (const r of runs) if (r.comparisonId) g.set(r.comparisonId, [...(g.get(r.comparisonId) ?? []), r]);
  for (const [k, rs] of g) if (rs.filter((r) => r.status !== "rejected").length <= 1) g.delete(k);
  return g;
}

function PhotosStep({ ctx }: { ctx: Ctx }) {
  const { p, eng } = ctx;
  const cards = eng?.engines.photo ?? [];
  const [engine, setEngine] = useState(p.defaults.photo.engine);
  const [backdrop, setBackdrop] = useState(p.defaults.photo.backdrop);
  const [colour, setColour] = useState(p.colours[0]?.colour ?? "");
  const [comparing, setComparing] = useState(false);
  const open = useMemo(() => openComparisons(p.runs.filter((r) => r.step === "photo")), [p.runs]);
  const inOpenCompare = (r: Run) => !!r.comparisonId && open.has(r.comparisonId);
  const c = p.colours.find((x) => x.colour === colour) ?? p.colours[0];
  const spec = cards.find((x) => x.id === engine);
  const latest = (view: string) => p.runs.find((r) => r.step === "photo" && r.params.colour === c?.colour && r.params.view === view && !inOpenCompare(r) && r.status !== "rejected");
  const live = (view: string, src: string) => p.approved.find((a) => a.colour === c?.colour && a.sourceUrl === src) ?? p.approved.find((a) => a.colour === c?.colour && a.view === view);

  return (
    <Step
      title="Photos"
      hint="Each colour's supplier photos, made into our studio photos. Cutout keeps the supplier's exact product; AI re-shoots are colour-locked and checked by a second AI for any change to the product."
      actions={
        <span className="flex flex-wrap items-end gap-3">
          <EngineSelect label="Engine" value={engine} onChange={setEngine} cards={cards} />
          <label className="flex flex-col gap-1">
            <span className="text-meta font-semibold uppercase tracking-wide text-ink-muted">Backdrop</span>
            <select value={backdrop} onChange={(e) => setBackdrop(e.target.value)} className="h-9 rounded-md border border-rule bg-white px-2 text-meta">
              {(eng?.backdrops ?? [{ name: backdrop, label: backdrop, colour: [245, 245, 242] as [number, number, number] }]).map((b) => <option key={b.name} value={b.name}>{b.name} — {b.label}</option>)}
            </select>
          </label>
        </span>
      }
    >
      {p.colours.length === 0 ? <p className="text-label text-ink-muted">The supplier sends no colour photos for this product.</p> : (
        <>
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Colour">
            {p.colours.map((x) => {
              const done = p.approved.filter((a) => a.colour === x.colour).length;
              return (
                <button key={x.colour} type="button" aria-pressed={x.colour === c?.colour} onClick={() => setColour(x.colour)}
                  className={cn("rounded-full border px-3 py-1 text-meta focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus", x.colour === c?.colour ? "border-ink bg-ink text-white" : "border-rule bg-white hover:bg-paper")}>
                  {x.colour}{done ? ` · ${done} live` : ""}
                </button>
              );
            })}
          </div>
          {c && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg bg-paper px-4 py-3">
              <span className="text-meta text-ink-muted">
                {spec ? `${spec.label}: about ${money(spec.costUsd)} and ${duration(spec.seconds)} a photo.` : ""} Backdrop <span className="inline-block h-3 w-3 rounded-sm border border-rule align-middle" style={{ background: hex(eng?.backdrops.find((b) => b.name === backdrop)?.colour ?? [245, 245, 242]) }} aria-hidden /> {backdrop}.
              </span>
              <span className="ml-auto flex flex-wrap gap-2">
                <Button size="sm" variant="outline" disabled={!spec?.ready || !p.workbenchReady} onClick={() => void ctx.runs({ photo: { engines: [engine], backdrop, colour: c.colour } }, `Making every view of ${c.colour}.`)}>
                  <Play className="h-3.5 w-3.5" aria-hidden /> Run all views of {c.colour}
                </Button>
                <Button size="sm" variant="outline" disabled={!p.workbenchReady} onClick={() => setComparing((x) => !x)} aria-expanded={comparing}>
                  <Scale className="h-3.5 w-3.5" aria-hidden /> Compare engines
                </Button>
              </span>
            </div>
          )}
          {comparing && c && <ComparePhotoSetup ctx={ctx} colour={c.colour} views={c.views.map((v) => v.view)} backdrop={backdrop} onDone={() => setComparing(false)} />}
          {[...open.values()].map((rs) => <PhotoComparison key={rs[0]!.comparisonId!} ctx={ctx} runs={rs} />)}
          {c && (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {c.views.map((v) => (
                <PhotoCell key={v.view} ctx={ctx} view={v.view} original={v.original} run={latest(v.view)} live={live(v.view, v.sourceUrl)}
                  onRun={() => void ctx.runs({ photo: { engines: [v.view === "detail" ? "cutout" : engine], backdrop, colour: c.colour, view: v.view } }, `Making the ${VIEW_LABEL[v.view]?.toLowerCase() ?? v.view} of ${c.colour}.`)}
                  canRun={!!p.workbenchReady && (v.view === "detail" ? !!cards.find((x) => x.id === "cutout")?.ready : !!spec?.ready)}
                  engineNote={v.view === "detail" && spec?.kind === "ai" ? "A close-up is always cut from the supplier's photo." : null} />
              ))}
            </div>
          )}
        </>
      )}
    </Step>
  );
}

function Img({ src, alt }: { src: string | null | undefined; alt: string }) {
  return src
    ? <a href={src} target="_blank" rel="noreferrer" className="block"><img src={src} alt={alt} loading="lazy" className="aspect-[4/5] w-full rounded bg-paper object-contain" /></a>
    : <div className="flex aspect-[4/5] w-full items-center justify-center rounded bg-paper text-center text-meta text-ink-muted">Not made yet</div>;
}

function PhotoCell({ ctx, view, original, run, live, onRun, canRun, engineNote }: {
  ctx: Ctx; view: string; original: string; run: Run | undefined; live: { studioUrl: string } | undefined; onRun: () => void; canRun: boolean; engineNote: string | null;
}) {
  const ours = run?.outputUrl ?? (run ? null : live?.studioUrl);
  const reasons = run?.result.reasons ?? [];
  const cost = run?.costUsd != null ? `${money(run.costUsd)} · ${duration(run.seconds ?? 0)}` : null;
  return (
    <figure className="flex flex-col gap-2 rounded-lg border border-rule p-3">
      <figcaption className="flex flex-wrap items-center gap-2">
        <span className="text-meta font-semibold">{VIEW_LABEL[view] ?? view}</span>
        {run ? <Chip run={run} /> : live ? <span className="rounded bg-ok-bg px-1.5 py-0.5 text-[12px] font-semibold text-ok">Live</span> : null}
        {run && <span className="ml-auto text-[12px] text-ink-muted">{ctx.eng?.engines.photo.find((e) => e.id === run.engine)?.label.split(" (")[0] ?? run.engine}</span>}
      </figcaption>
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-1"><Img src={original} alt={`Supplier photo, ${view}`} /><span className="text-[12px] text-ink-muted">Supplier</span></div>
        <div className="flex flex-col gap-1"><Img src={ours} alt={`Our studio photo, ${view}`} /><span className="text-[12px] text-ink-muted">Ours</span></div>
      </div>
      {run && (run.status === "failed" || run.status === "error") && reasons.length > 0 && <p className="text-meta text-risk">{reasons.join("; ")}</p>}
      {run && photoScores(run).length > 0 && <p className="text-[12px] text-ink-muted">{photoScores(run).join(" · ")}{cost ? ` · ${cost}` : ""}</p>}
      {engineNote && <p className="text-[12px] text-ink-muted">{engineNote}</p>}
      <div className="mt-auto flex flex-wrap gap-2">
        {run?.status === "passed" && <Button size="sm" onClick={() => void ctx.post(`/studio/workbench/runs/${run.id}/accept`, {}, "Accepted. It is the live photo for that colour now.")}><Check className="h-3.5 w-3.5" aria-hidden /> Accept</Button>}
        {run && ["passed", "failed", "error"].includes(run.status) && <Button size="sm" variant="ghost" onClick={() => void ctx.post(`/studio/workbench/runs/${run.id}/reject`)}><X className="h-3.5 w-3.5" aria-hidden /> Reject</Button>}
        <Button size="sm" variant="outline" className="ml-auto" disabled={!canRun || (run ? isOpen(run) : false)} onClick={onRun}>
          <Play className="h-3.5 w-3.5" aria-hidden /> {run || live ? "Run again" : "Run"}
        </Button>
      </div>
    </figure>
  );
}

function ComparePhotoSetup({ ctx, colour, views, backdrop, onDone }: { ctx: Ctx; colour: string; views: string[]; backdrop: string; onDone: () => void }) {
  const cards = (ctx.eng?.engines.photo ?? []);
  const [view, setView] = useState(views.find((v) => v !== "detail") ?? views[0] ?? "front");
  const [pick, setPick] = useState<string[]>(cards.filter((c) => c.ready).slice(0, 2).map((c) => c.id));
  const toggle = (id: string) => setPick((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length >= 3 ? s : [...s, id]));
  const chosen = cards.filter((c) => pick.includes(c.id));
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-ink bg-white p-4">
      <p className="text-label font-semibold">Compare engines on one photo of {colour}</p>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-meta">View
          <select value={view} onChange={(e) => setView(e.target.value)} className="h-8 rounded-md border border-rule bg-white px-2 text-meta">
            {views.filter((v) => v !== "detail").map((v) => <option key={v} value={v}>{VIEW_LABEL[v] ?? v}</option>)}
          </select>
        </label>
        {cards.map((c) => (
          <label key={c.id} className={cn("flex items-center gap-2 rounded-md border px-2 py-1 text-meta", pick.includes(c.id) ? "border-ink" : "border-rule", !c.ready && "opacity-50")} title={c.ready ? c.blurb : `Add a key: ${c.missing.join(", ")}`}>
            <input type="checkbox" checked={pick.includes(c.id)} disabled={!c.ready} onChange={() => toggle(c.id)} /> {c.label}{!c.ready ? " — add a key" : ""}
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-meta text-ink-muted">About {money(chosen.reduce((s, c) => s + c.costUsd, 0))} and {duration(Math.max(0, ...chosen.map((c) => c.seconds)))} for {chosen.length} engines side by side.</span>
        <Button size="sm" className="ml-auto" disabled={chosen.length < 2} onClick={async () => { if (await ctx.runs({ photo: { engines: pick, backdrop, colour, view } }, "Comparing. Each engine's result appears below with its checks, cost and time.")) onDone(); }}>
          <Scale className="h-3.5 w-3.5" aria-hidden /> Compare {chosen.length} engines
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>Cancel</Button>
      </div>
    </div>
  );
}

function PhotoComparison({ ctx, runs }: { ctx: Ctx; runs: Run[] }) {
  const first = runs[0]!;
  const allDone = runs.every((r) => !isOpen(r));
  const original = ctx.p.colours.find((c) => c.colour === first.params.colour)?.views.find((v) => v.view === first.params.view)?.original;
  return (
    <section className="flex flex-col gap-3 rounded-lg border border-ink p-4" aria-label="Engine comparison">
      <p className="text-label font-semibold">Comparing engines — {first.params.colour}, {VIEW_LABEL[first.params.view ?? ""] ?? first.params.view}</p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex flex-col gap-1"><Img src={original} alt="Supplier photo" /><span className="text-[12px] text-ink-muted">Supplier</span></div>
        {runs.map((r) => (
          <div key={r.id} className={cn("flex flex-col gap-1 rounded border p-2", r.status === "rejected" ? "border-rule opacity-50" : "border-rule")}>
            <Img src={r.outputUrl} alt={`Result from ${r.engine}`} />
            <span className="text-meta font-semibold">{ctx.eng?.engines.photo.find((e) => e.id === r.engine)?.label ?? r.engine}</span>
            <Chip run={r} />
            {(r.result.reasons ?? []).length > 0 && <span className="text-[12px] text-risk">{r.result.reasons!.join("; ")}</span>}
            <span className="text-[12px] text-ink-muted">{[...photoScores(r), r.costUsd != null ? money(r.costUsd) : null, r.seconds != null ? duration(r.seconds) : null].filter(Boolean).join(" · ")}</span>
            <Button size="sm" variant={r.status === "passed" ? "primary" : "outline"} disabled={!allDone || r.status === "rejected"} title={r.status === "passed" ? "Records the choice and makes this the live photo" : "Records the choice; a photo that failed its checks is not put live"}
              onClick={() => void ctx.post(`/studio/workbench/comparisons/${r.comparisonId}/winner`, { runId: r.id }, r.status === "passed" ? "Winner recorded and made live." : "Winner recorded. It failed its checks, so it was not put live.")}>
              <Check className="h-3.5 w-3.5" aria-hidden /> Pick this one
            </Button>
          </div>
        ))}
      </div>
      {!allDone && <p className="text-meta text-ink-muted">Waiting for every engine to finish before you pick.</p>}
    </section>
  );
}

// ─── Description ─────────────────────────────────────────────────────────────

function DescriptionStep({ ctx }: { ctx: Ctx }) {
  const { p, eng } = ctx;
  const cards = eng?.engines.text ?? [];
  const [model, setModel] = useState(p.defaults.description);
  const [compare, setCompare] = useState<string[] | null>(null);
  const descRuns = p.runs.filter((r) => r.step === "description");
  const open = openComparisons(descRuns);
  const draft = descRuns.find((r) => !(r.comparisonId && open.has(r.comparisonId)) && r.status !== "rejected" && r.status !== "accepted");
  const spec = cards.find((c) => c.id === model);
  return (
    <Step
      title="Description"
      hint="Written only from the supplier's facts. Checked for the supplier's name, invented numbers and any talk of price before you can accept it."
      actions={
        <span className="flex flex-wrap items-end gap-2">
          <EngineSelect label="Model" value={model} onChange={setModel} cards={cards} />
          <Button size="sm" disabled={!spec?.ready || !p.workbenchReady || (draft ? isOpen(draft) : false)} onClick={() => void ctx.runs({ description: { engines: [model] } }, "Writing a draft.")}>
            <Play className="h-3.5 w-3.5" aria-hidden /> Write a draft · ~{money(spec?.costUsd ?? 0)}
          </Button>
          <Button size="sm" variant="outline" disabled={!p.workbenchReady} onClick={() => setCompare((c) => (c ? null : cards.filter((x) => x.ready).map((x) => x.id)))} aria-expanded={!!compare}>
            <Scale className="h-3.5 w-3.5" aria-hidden /> Compare models
          </Button>
        </span>
      }
    >
      {compare && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-ink p-3">
          {cards.map((c) => (
            <label key={c.id} className={cn("flex items-center gap-2 text-meta", !c.ready && "opacity-50")}>
              <input type="checkbox" checked={compare.includes(c.id)} disabled={!c.ready} onChange={() => setCompare((s) => (s!.includes(c.id) ? s!.filter((x) => x !== c.id) : [...s!, c.id]))} /> {c.label} · ~{money(c.costUsd)}
            </label>
          ))}
          <Button size="sm" className="ml-auto" disabled={compare.length < 2} onClick={async () => { if (await ctx.runs({ description: { engines: compare } }, "Each model is writing a draft.")) setCompare(null); }}>Compare {compare.length}</Button>
        </div>
      )}
      {[...open.values()].map((rs) => (
        <section key={rs[0]!.comparisonId!} className="grid gap-3 rounded-lg border border-ink p-4 md:grid-cols-3" aria-label="Model comparison">
          {rs.map((r) => (
            <div key={r.id} className={cn("flex flex-col gap-2", r.status === "rejected" && "opacity-50")}>
              <span className="flex items-center gap-2 text-meta font-semibold">{cards.find((c) => c.id === r.engine)?.label ?? r.engine} <Chip run={r} /></span>
              <p className="whitespace-pre-line text-label">{r.result.text ?? (isOpen(r) ? "…" : "No usable draft.")}</p>
              {(r.result.reasons ?? []).length > 0 && <p className="text-[12px] text-risk">{r.result.reasons!.join("; ")}</p>}
              <span className="text-[12px] text-ink-muted">{[r.result.checks?.words ? `${r.result.checks.words} words` : null, r.costUsd != null ? money(r.costUsd) : null, r.seconds != null ? duration(r.seconds) : null].filter(Boolean).join(" · ")}</span>
              <Button size="sm" variant="outline" disabled={rs.some(isOpen) || r.status === "rejected"} onClick={() => void ctx.post(`/studio/workbench/comparisons/${r.comparisonId}/winner`, { runId: r.id }, "Winner recorded. Review and accept its words below.")}>
                <Check className="h-3.5 w-3.5" aria-hidden /> Pick this one
              </Button>
            </div>
          ))}
        </section>
      ))}
      <div className="grid gap-5 lg:grid-cols-3">
        <section className="flex flex-col gap-2">
          <h4 className="text-meta font-semibold uppercase tracking-wide text-ink-muted">The supplier's words</h4>
          <p className="max-h-[260px] overflow-y-auto whitespace-pre-line text-label text-ink-muted">{p.descriptionStep.supplier?.trim() || "None."}</p>
        </section>
        <section className="flex flex-col gap-2">
          <h4 className="text-meta font-semibold uppercase tracking-wide text-ink-muted">On the website now {p.descriptionStep.source !== "supplier" ? `(${p.descriptionStep.source === "edited" ? "ours, edited" : "ours"})` : "(the supplier's)"}</h4>
          <p className="max-h-[260px] overflow-y-auto whitespace-pre-line text-label">{p.descriptionStep.current?.trim() || "No description."}</p>
        </section>
        <DraftEditor key={draft?.id ?? "none"} ctx={ctx} draft={draft} />
      </div>
    </Step>
  );
}

function DraftEditor({ ctx, draft }: { ctx: Ctx; draft: Run | undefined }) {
  const [text, setText] = useState(draft?.result.text ?? "");
  const edited = !!draft?.result.text && text.replace(/\s+/g, " ").trim() !== draft.result.text;
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;
  const canAccept = !!draft && !!text.trim() && text.trim().length >= 40 && (draft.status === "passed" || (draft.status === "failed" && edited));
  return (
    <section className="flex flex-col gap-2">
      <h4 className="flex items-center gap-2 text-meta font-semibold uppercase tracking-wide text-ink-muted">
        Our draft {draft && <Chip run={draft} />} {edited && <span className="rounded bg-warn-bg px-1.5 text-[12px] normal-case tracking-normal text-warn">edited</span>}
      </h4>
      {!draft ? <p className="text-label text-ink-muted">No draft yet. Pick a model and write one.</p> : isOpen(draft) ? (
        <p className="flex items-center gap-2 text-label text-ink-muted"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Writing…</p>
      ) : (
        <>
          {draft.result.text != null && (
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} aria-label="Our draft description"
              className="w-full resize-y rounded-md border border-rule p-3 text-label leading-relaxed focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus" />
          )}
          {(draft.result.reasons ?? []).length > 0 && <p className="text-meta text-risk">{draft.status === "failed" ? "Failed checks: " : ""}{draft.result.reasons!.join("; ")}{draft.status === "failed" && draft.result.text ? ". Fix the words and accept your edit." : ""}</p>}
          <span className="text-[12px] text-ink-muted">{words} words · {ctx.eng?.engines.text.find((e) => e.id === draft.engine)?.label ?? draft.engine}{draft.costUsd != null ? ` · ${money(draft.costUsd)} · ${duration(draft.seconds ?? 0)}` : ""}</span>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" disabled={!canAccept} onClick={() => void ctx.post(`/studio/workbench/runs/${draft.id}/accept`, edited ? { text } : {}, "Accepted. The website shows the new description now.")}>
              <Check className="h-3.5 w-3.5" aria-hidden /> {edited ? "Accept my edit" : "Accept"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void ctx.post(`/studio/workbench/runs/${draft.id}/reject`)}><X className="h-3.5 w-3.5" aria-hidden /> Reject</Button>
            {edited && <button type="button" className="text-meta underline" onClick={() => setText(draft.result.text ?? "")}>Undo my edits</button>}
          </div>
        </>
      )}
    </section>
  );
}

// ─── Name, SKU, shelf ────────────────────────────────────────────────────────

function NameStep({ ctx }: { ctx: Ctx }) {
  const s = ctx.p.nameStep;
  const [name, setName] = useState(s.proposed);
  const changed = name.replace(/\s+/g, " ").trim() !== s.current;
  return (
    <Step title="Name" hint="The supplier's name, tidied: no style numbers, codes or stray marks. Brands stay.">
      <dl className="grid gap-1 text-meta">
        <dt className="text-ink-muted">Supplier's name</dt><dd>{s.supplier ?? "—"}</dd>
        <dt className="text-ink-muted">Ours now</dt><dd className="font-semibold">{s.current}</dd>
      </dl>
      <label className="flex flex-col gap-1">
        <span className="text-meta font-semibold uppercase tracking-wide text-ink-muted">Proposed</span>
        <input value={name} onChange={(e) => setName(e.target.value)} className="h-9 rounded-md border border-rule px-2 text-label focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus" />
      </label>
      {s.fixes.length > 0 && <p className="text-[12px] text-ink-muted">Fixed: {s.fixes.join(", ").replace(/_/g, " ")}</p>}
      <Button size="sm" className="self-start" disabled={!changed || name.trim().length < 3} onClick={() => void ctx.post(`/studio/workbench/products/${ctx.p.id}/name`, { name: name.trim() }, "Name saved.")}>
        <Check className="h-3.5 w-3.5" aria-hidden /> {changed ? "Use this name" : "Already in use"}
      </Button>
    </Step>
  );
}

function SkuStep({ ctx }: { ctx: Ctx }) {
  const s = ctx.p.skuStep;
  return (
    <Step title="SKU" hint="Our catalogue SKU, by the same rules as every other product.">
      <p className="text-[22px] font-semibold tabular-nums">{s.listing ?? "None yet"}</p>
      <p className="text-meta text-ink-muted">{s.variants} colour × size SKUs{s.variantsMissing ? ` · ${s.variantsMissing} missing` : ""}</p>
      {s.why && <p className="text-meta text-warn">{s.why}</p>}
      {s.canAssign && (
        <Button size="sm" className="self-start" onClick={() => void ctx.post(`/studio/workbench/products/${ctx.p.id}/sku`, {}, "SKU assigned.")}>
          <Check className="h-3.5 w-3.5" aria-hidden /> {s.listing ? `Add the ${s.variantsMissing} missing` : "Assign"}
        </Button>
      )}
    </Step>
  );
}

interface ShelfOption { slug: string; name: string; parent: string | null }

function ShelfStep({ ctx }: { ctx: Ctx }) {
  const s = ctx.p.categoryStep;
  const [options, setOptions] = useState<ShelfOption[]>([]);
  const [to, setTo] = useState("");
  useEffect(() => { api<{ shelves: ShelfOption[] }>("/ingestor/shelves/options").then((r) => setOptions(r.shelves)).catch(() => {}); }, []);
  const move = (slug: string, ok: string) => void ctx.post(`/studio/workbench/products/${ctx.p.id}/shelf`, { toSlug: slug }, ok);
  return (
    <Step title="Shelf" hint="Where it sits on the website. A move here is kept when the catalogue is re-filed each night.">
      <p className="text-label"><span className="text-ink-muted">Now: </span><b className="font-semibold">{s.current?.name ?? "Not on a shelf"}</b>{s.setByHand ? <span className="text-meta text-ink-muted"> · set by hand</span> : null}</p>
      {s.suspicion && <p className="self-start rounded bg-warn-bg px-1.5 py-0.5 text-[12px] font-semibold text-warn">{s.suspicion}</p>}
      {s.suggestion ? (
        <div className="flex flex-wrap items-center gap-2 rounded bg-paper p-2 text-meta">
          <span>The classifier says <b className="font-semibold">{s.suggestion.name}</b>{s.suggestion.confidence ? ` (${s.suggestion.confidence} confidence, from the ${s.suggestion.decidedBy})` : ""}.</span>
          <Button size="sm" onClick={() => move(s.suggestion!.slug, `Moved to ${s.suggestion!.name}.`)}><ArrowRight className="h-3.5 w-3.5" aria-hidden /> Move there</Button>
        </div>
      ) : <p className="text-meta text-ink-muted">The classifier agrees with this shelf.</p>}
      <div className="flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor={`shelf-${ctx.p.id}`}>Move to another shelf</label>
        <select id={`shelf-${ctx.p.id}`} value={to} onChange={(e) => setTo(e.target.value)} className="h-8 max-w-[220px] rounded-md border border-rule bg-white px-2 text-meta">
          <option value="">Another shelf…</option>
          {options.map((o) => <option key={o.slug} value={o.slug} disabled={o.slug === s.current?.slug}>{o.parent ? `${o.parent} › ` : ""}{o.name}</option>)}
        </select>
        <Button size="sm" variant="outline" disabled={!to} onClick={() => move(to, "Moved.")}>Move</Button>
        {s.current && !s.setByHand && <Button size="sm" variant="ghost" onClick={() => move(s.current!.slug, "Kept on this shelf.")}>Keep here</Button>}
      </div>
    </Step>
  );
}
